import { create } from 'zustand';
import type { ProductWithCategories } from '@/lib/api/products';

// ========== TYPES ==========

export interface ProductPopupState {
  // UI State
  isOpen: boolean;
  
  // Current Product
  product: ProductWithCategories | null;
  
  // View Stack for navigating through set ingredients
  viewStack: ProductWithCategories[];
  
  // Loading states
  isLoadingIngredients: boolean;
  setIngredients: ProductWithCategories[];
  
  // Quantity
  quantity: number;
  
  // ========== ACTIONS ==========
  
  // Open/Close
  openPopup: (product: ProductWithCategories) => void;
  closePopup: () => void;
  
  // Navigation
  pushProduct: (product: ProductWithCategories) => void;
  popProduct: () => void;
  
  // Set Ingredients
  setLoadingIngredients: (loading: boolean) => void;
  setSetIngredients: (ingredients: ProductWithCategories[]) => void;
  
  // Quantity
  setQuantity: (quantity: number) => void;
  incrementQuantity: () => void;
  decrementQuantity: () => void;
  
  // Reset
  reset: () => void;
}

// ========== INITIAL STATE ==========

const initialState = {
  isOpen: false,
  product: null,
  viewStack: [],
  isLoadingIngredients: false,
  setIngredients: [],
  quantity: 1,
};

// ========== STORE ==========

export const useProductPopupStore = create<ProductPopupState>((set, get) => ({
  ...initialState,

  // Open popup with a product
  openPopup: (product) => {
    set({
      isOpen: true,
      product,
      viewStack: [],
      setIngredients: [],
      quantity: 1,
    });

    // ── Behavioral tracking: product_viewed ──
    import('@/lib/source-tracker').then(({ captureEvent }) => {
      captureEvent('product_viewed', {
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku || null,
        price: product.price,
        category_ids: product.categories?.map((c) => c.id) || [],
      }, { value: product.price, currency: 'RUB' });
    }).catch(() => { /* ignore */ });
  },

  // Close popup
  closePopup: () => {
    set({
      isOpen: false,
    });
    // Delay reset to allow exit animation to complete
    setTimeout(() => {
      set(initialState);
    }, 450);
  },

  // Push a product to view stack (for navigating into set ingredients)
  pushProduct: (product) => {
    const { product: currentProduct, viewStack } = get();
    if (currentProduct) {
      set({
        viewStack: [...viewStack, currentProduct],
        product,
        quantity: 1,
      });
    }
  },

  // Pop product from view stack (go back)
  popProduct: () => {
    const { viewStack } = get();
    if (viewStack.length > 0) {
      const newStack = [...viewStack];
      const previousProduct = newStack.pop();
      set({
        viewStack: newStack,
        product: previousProduct || null,
        quantity: 1,
      });
    }
  },

  // Set loading state for set ingredients
  setLoadingIngredients: (loading) => {
    set({ isLoadingIngredients: loading });
  },

  // Set the set ingredients
  setSetIngredients: (ingredients) => {
    set({ setIngredients: ingredients });
  },

  // Set quantity
  setQuantity: (quantity) => {
    set({ quantity: Math.max(1, Math.min(99, quantity)) });
  },

  // Increment quantity
  incrementQuantity: () => {
    const { quantity } = get();
    if (quantity < 99) {
      set({ quantity: quantity + 1 });
    }
  },

  // Decrement quantity
  decrementQuantity: () => {
    const { quantity } = get();
    if (quantity > 1) {
      set({ quantity: quantity - 1 });
    }
  },

  // Full reset
  reset: () => {
    set(initialState);
  },
}));

// ========== SELECTORS ==========

export const selectIsPopupOpen = (state: ProductPopupState) => state.isOpen;
export const selectCurrentProduct = (state: ProductPopupState) => state.product;
export const selectViewStack = (state: ProductPopupState) => state.viewStack;
export const selectIsViewingNested = (state: ProductPopupState) => state.viewStack.length > 0;
export const selectOriginalProduct = (state: ProductPopupState) => 
  state.viewStack.length > 0 ? state.viewStack[0] : state.product;
export const selectSetIngredients = (state: ProductPopupState) => state.setIngredients;
export const selectIsLoadingIngredients = (state: ProductPopupState) => state.isLoadingIngredients;
export const selectQuantity = (state: ProductPopupState) => state.quantity;

